import { API_ENDPOINT } from "@/constants/appConstants";
import { HEADER_QUERY_KEY } from "@/lib/queryKeys";
import { getSiteProps } from "@/utils";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

import {
  FormattedData,
  HeaderApiResponseData,
  HeaderLinkReturnType,
} from "./types";
import { SHOW_TOP_LOCATION, changeArrayToObjFormat } from "./utils";

const fetchData = async (path: string) => {
  try {
    const response = await fetch(path);

    if (
      response.status === 401 &&
      window.location.hostname &&
      !window.location.hostname.includes("localhost") // to avoid multiple rendering
    ) {
      window.location.href = "/rio/logout";
      return;
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch data from ${path}`);
    }
    return await response.json();
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const fetchCartCount = async () => {
  const cartData = await fetchData("/seeker-profile/api/cartcount");
  if (cartData?.Status === 200 && typeof cartData?.Response === "number") {
    return {
      cartCount: cartData?.Response,
    };
  }
  return {
    cartCount: 0,
  };
};

export const fetchNotificationCount = async () => {
  const notificationData = await fetchData(
    "/seeker-profile/api/notificationcount",
  );
  if (!notificationData) return { notificationCount: 0 };
  return {
    notificationCount: notificationData?.errorMessage
      ? 0
      : notificationData?.total || 0,
  };
};

export const fetchProfileData = async (apiEndpoint: string) => {
  const userData = await fetchData("/seeker-profile/api/rioMe");
  if (!userData) {
    return { userName: "Seeker", userProfileImage: "" };
  }
  const userName = userData?.displayName || "Seeker";
  const userProfileImage = userData?.avatarPath
    ? `${apiEndpoint}${userData.avatarPath}`
    : "";
  const updatedAt = userData.updatedAt;
  return { userName, userProfileImage, updatedAt };
};

export const fetchHeaderNavLinks = async (): Promise<HeaderLinkReturnType> => {
  const navLinks = await fetchData("/seeker-profile/api/header");
  if (!navLinks) {
    return { navLinks: [] };
  }
  return { navLinks: navLinks };
};

export const fetchTopLocations = async (
  host: string,
  isMobile: boolean,
): Promise<FormattedData[]> => {
  let locationsDefaultSlotData: FormattedData[] = [];
  const siteProps = getSiteProps(host);

  if (SHOW_TOP_LOCATION === siteProps?.site_map) {
    try {
      const res = await fetch("/seeker-profile/api/topLocations", {
        method: "GET",
        headers: {
          baseUrl: JSON.stringify(API_ENDPOINT(host).ravenApi.topLocationsUrl),
        },
      });
      const response = (await res.json()) as string[];
      if (response?.length !== 0) {
        const maxItemsToShowOnDesktop = 8;
        const locationsRes = response.slice(1) as string[];
        locationsDefaultSlotData.push(...changeArrayToObjFormat(locationsRes));
        if (
          !isMobile &&
          locationsDefaultSlotData.length > maxItemsToShowOnDesktop
        ) {
          locationsDefaultSlotData.length = maxItemsToShowOnDesktop;
        }

        if (isMobile) {
          locationsDefaultSlotData = locationsDefaultSlotData.slice(0, 4);
        }
      }
    } catch (error) {
      console.error("Error fetching top locations:", error);
    }
  }
  return locationsDefaultSlotData;
};

export const fetchHeaderData = async (apiEndpoint: string) => {
  try {
    const [cartResponse, notificationResponse, profileDataResponse] =
      await Promise.all([
        fetchCartCount(),
        fetchNotificationCount(),
        fetchProfileData(apiEndpoint),
      ]);

    return {
      isLoading: false,
      isError: false,
      data: {
        ...cartResponse,
        ...notificationResponse,
        ...profileDataResponse,
      },
    };
  } catch (error: any) {
    return {
      isLoading: false,
      isError: true,
      data: null,
    };
  }
};

export const getHeaderData = (
  apiEndpoint: string,
): UseQueryResult<HeaderApiResponseData> => {
  return useQuery({
    queryKey: [HEADER_QUERY_KEY],
    queryFn: () => fetchHeaderData(apiEndpoint),
  });
};
